import React, { ChangeEvent, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUpLoadIcon from "@mui/icons-material/FileUpload";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from '@mui/material';
import { BatchProject, PriorYearProject, excelColumnMaxLength, priorYearProjectColumnNames, priorYearProjectNote, samplePriorYearProject } from '../../scenario-modelling/TableData';
import * as Excel from "exceljs/dist/exceljs.min.js";
import saveAs from 'file-saver';
import { MRT_Cell, MRT_ColumnDef, MRT_ColumnFiltersState, MRT_FullScreenToggleButton, MRT_RowSelectionState, MRT_ShowHideColumnsButton, MRT_TableInstance, MRT_ToggleFiltersButton, MRT_VisibilityState, MaterialReactTable } from 'material-react-table';
import { statePriorYearBaselineConfig } from '../../scenario-modelling/componentsConfig/statePriorYearBaselineConfig';
import { useMsal } from "@azure/msal-react";
import {
  AppendStateProjectBatchUpload,
  CloseStateProjectUploadBatch,
  CreateStateProjectUploadBatch,
  GetLastUploadedProjectList,
  GetPriorYearProject,
  GetProjectBatchUpload
} from '../../../graphql/queries_pipelineEditor';
import { apiRequest } from '../../../services/utils/HttpUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LinearProgress from '@mui/material/LinearProgress';
import { formatCurrency, parseInvestment } from '../../../services/utils/excel';
import { getStates, getLocations, getProjectTypes, getSource, getSortedLocationByState } from '../../../services/utils/excelUtils';
import dayjs from 'dayjs';
import {getUserInfo} from "../../../graphql/queries_user_info";


const StatePriorYearBaseline = () => {
    const client = useQueryClient();
    const { data: userInfo } = getUserInfo();
    const [downloading, setDownloading] = useState(false);
    const [upLoadInfo, setUpLoadInfo] = useState({
        totalRecords: 0,
        totalInvestment: "",
        totalMissingProjectNameValue: "",
        totalMissingProjectTypeValue: "",
        totalMissingConstructionStartDateValue: "",
        totalMissingConstructionEndDateValue: "",
    })
    const [preViewData, setPreViewData] = useState<BatchProject[]>([]);
    const [statePipelineData, setStatePipelineData] = useState<PriorYearProject[]>([]);
    const [callLastUploadedProjectList, setCallLastUploadedProjectList] = useState(false);
    const [preViewFlag, setPreViewFlag] = useState(false);
    const [closeBatchFlag, setCloseBatchFlag] = useState(false);
    const [projectUploadBatchID, setProjectUploadBatchID] = useState<String>("");
    const [lastUploadedDate, setLastUploadedDate] = useState<string>("");
    const [lastUpdatedUser, setLastUpdatedUser] = useState<string>("");
    const { instance, accounts } = useMsal();
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [submitPopupOpen, setSubmitPopupOpen] = useState(false);
    const rerender = useReducer(() => ({}), {})[1];

    // table settings
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const tableInstanceRef = useRef<MRT_TableInstance<PriorYearProject | BatchProject>>(null);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
        {
            PeriodStartYear: false,
            PeriodEndYear: false,
            KM: false,
            MW: false,
            M2: false,
            sa4_code: false,
            sa4_name: false,
            CommonwealthFunding: false
        },
    );
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const [validationErrors, setValidationErrors] = useState<{
        [cellId: string]: string;
    }>({});

    const { data: priorYearProjectList, isLoading: isPriorYearProjectListLoading, isFetching } = useQuery(
        {
            queryKey: ["priorYearProjectList"],
            queryFn: () => {
                return apiRequest(
                    {
                        query: GetPriorYearProject,
                        variables: {
                            // @ts-ignore
                            inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction,
                            inScenarioID: ""
                        },
                        instance: instance,
                        accounts: accounts
                    })
            },
            refetchOnWindowFocus: false,
            // @ts-ignore
            enabled: !callLastUploadedProjectList && !! userInfo?.executeGetUser[0].Jurisdiction,
            onSuccess: (data) => {
                //@ts-ignore
                const priorYearData = data?.executeGetPriorYearProjectList
                if (priorYearData && !callLastUploadedProjectList) {
                    setStatePipelineData(priorYearData.map(project => { return { ...project, "LastUploadedDate": "No upload data" } }));
                }
            }
        });

    // react query - get last uploaded project list
    const { data: lastUploadedProjectList, isLoading: isLastUploadedProjectListLoading } = useQuery(
        {
            queryKey: ["lastUploadedProjectList"],
            queryFn: () => {
                return apiRequest(
                    {
                        query: GetLastUploadedProjectList,
                        variables: {
                            // @ts-ignore
                            inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction,
                            inScenarioID: ""
                        },
                        instance: instance,
                        accounts: accounts
                    })
            },
            // @ts-ignore
            enabled: callLastUploadedProjectList && !!userInfo?.executeGetUser[0].Jurisdiction,
            onSuccess: (data) => {
                //@ts-ignore
                const lastUploadedData = data?.executeGetLastUploadedProjectList
                if (lastUploadedData && callLastUploadedProjectList) {
                    setStatePipelineData(lastUploadedData.map(project => { return { ...project, "LastUploadedDate": dayjs(lastUploadedDate).format('DD/MM/YYYY HH:mm:ss') } }));
                }
            }
        }
    )

    const { data: getProjectBatchHeader, isLoading: isGetProjectBatchHeaderLoading } = useQuery(
        {
            queryKey: ["getStateUploadBatchHeader"],
            queryFn: () => {
                return apiRequest(
                    {
                        query: GetProjectBatchUpload,
                        variables: {
                            // @ts-ignore
                            inUserSID: userInfo?.executeGetUser[0].UserSID
                        },
                        instance: instance,
                        accounts: accounts
                    })
            },
            // @ts-ignore
            enabled: !!userInfo?.executeGetUser[0].UserSID,
            onSuccess: (data) => {
                //@ts-ignore
                const lastUploadedDate = data?.executeGetProjectBatchUpload[0]?.LastUploadedDate || "";
                //@ts-ignore
                const lastUpdatedUser = data?.executeGetProjectBatchUpload[0]?.LastUploadedBy || "";
                //@ts-ignore
                const projectsUploaded = data?.executeGetProjectBatchUpload[0]?.ProjectsUploaded || 0;
                setLastUploadedDate(dayjs(lastUploadedDate).format('DD/MM/YYYY HH:mm:ss'));
                setLastUpdatedUser(lastUpdatedUser);
                if (projectsUploaded !== 0) {
                  setCallLastUploadedProjectList(true);
                }
            }
        }
    )
    const { mutate: createProjectUpload, data: uploadBatchHeader, isSuccess: isUploadBatchHeaderSuccess } = useMutation(
        {
            mutationFn: () => {
              return apiRequest(
                {
                  query: CreateStateProjectUploadBatch,
                  variables: {
                    // @ts-ignore
                    inUserSID: userInfo?.executeGetUser[0].UserSID
                  },
                  instance: instance,
                  accounts: accounts
                })
            },
            onSuccess: (data) => {
                //@ts-ignore
                const batchID = data?.executeCreateProjectUploadBatch[0]?.BatchID || 0;
                //@ts-ignore
                const lastUploadedDate = data?.executeCreateProjectUploadBatch[0]?.LastUploadedDate || "";
                //@ts-ignore
                const lastUpdatedUser = data?.executeCreateProjectUploadBatch[0]?.LastUploadedBy || "";
                //@ts-ignore
                const projectsUploaded = data?.executeCreateProjectUploadBatch[0]?.ProjectsUploaded;
                setProjectUploadBatchID(batchID);
                setLastUploadedDate(dayjs(lastUploadedDate).format('DD/MM/YYYY HH:mm:ss'));
                setLastUpdatedUser(lastUpdatedUser);
                if (projectsUploaded !== 0) {
                    setCallLastUploadedProjectList(true);
                }
                validateUploadProjects.mutate({
                    inProjectBatchID: batchID,
                    inProjectJSON: JSON.stringify(preViewData)
                });


            }
        });

    const { data: uploadBatchClose, isLoading: isUploadBatchCloseLoading } = useQuery(
        {
            queryKey: ["stateUploadBatchClose"],
            queryFn: () => {
                return apiRequest(
                    {
                        query: CloseStateProjectUploadBatch,
                        variables: {
                            inProjectBatchID: projectUploadBatchID,
                        },
                        instance: instance,
                        accounts: accounts
                    })
            },
            enabled: closeBatchFlag && !!projectUploadBatchID,
            onSuccess: () => {
                // Close the batch and reset the state
                client.invalidateQueries(["getStateUploadBatchHeader"])
                setProjectUploadBatchID("");
                setCloseBatchFlag(false);
                setPreViewFlag(false);
                setPreViewData([]);
                setUpLoadInfo({
                    totalRecords: 0,
                    totalInvestment: "",
                    totalMissingProjectNameValue: "",
                    totalMissingProjectTypeValue: "",
                    totalMissingConstructionStartDateValue: "",
                    totalMissingConstructionEndDateValue: ""
                })

            }
        });



    const validateUploadProjects = useMutation(["validateUploadProjects"], {
        mutationFn: (variables: any) => {
            return apiRequest(
                {
                    query: AppendStateProjectBatchUpload,
                    variables: variables,
                    instance: instance,
                    accounts: accounts,
                })
        },
        onSuccess: () => {
            setCloseBatchFlag(true);
            client.invalidateQueries(["lastUploadedProjectList"])

        }
    })

    //@ts-ignore
    const statePipelineListData = callLastUploadedProjectList ? lastUploadedProjectList?.executeGetLastUploadedProjectList ?? [samplePriorYearProject] : priorYearProjectList?.executeGetPriorYearProjectList ?? [samplePriorYearProject];
    const tempData = callLastUploadedProjectList ? lastUploadedProjectList : priorYearProjectList;
    const projectTypes = getProjectTypes(tempData);
    const locations = getLocations(tempData);
    const states = getStates(tempData);
    const source = getSource(tempData);


    const handleDownload = () => {
        setDownloading(true);
    }


    // submit uploaded data
    const handleUpload = async () => {
        setSubmitPopupOpen(true);

    };
    const handleConfirmSubmit = () => {
        // upload project creation
        createProjectUpload();
        // if (preViewFlag && !!projectUploadBatchID) {
        //     validateUploadProjects.mutate({
        //         inProjectBatchID: projectUploadBatchID,
        //         inProjectJSON: JSON.stringify(preViewData)
        //     });
        // }
        setSubmitPopupOpen(false);

    }

    const handleCancelSubmit = () => {
        setSubmitPopupOpen(false)
        setPreViewFlag(false);
    }

    // Upload Excel but not submit
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];


        if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                if (event.target && event.target.result) {
                    const workbook = new Excel.Workbook();
                    await workbook.xlsx.load(event.target.result as ArrayBuffer);
                    const worksheet = workbook.worksheets[0];
                    const jsonData: any[] = [];
                    let headers: string[] = [];
                    let totalInvestment = 0;
                    let totalMissingProjectNameValue = 0;
                    let totalMissingProjectTypeValue = 0;
                    let totalMissingConstructionStartDateValue = 0;
                    let totalMissingConstructionEndDateValue = 0;


                    worksheet.eachRow((row, rowNumber) => {
                        const rowValues = row.values;

                        // Row 5 is the header row
                        if (rowNumber === priorYearProjectNote.length + 1) {
                            headers = rowValues.slice(1); // Remove first empty element
                        } else if (rowNumber > priorYearProjectNote.length + 1) {
                            const rowObject: { [key: string]: any } = {};
                            const investmentColumnIndex = headers.indexOf("Investment ($)") + 1;
                            headers.forEach((header, index) => {
                                let currentInvestment = parseInvestment(rowValues[investmentColumnIndex]);
                                let cellValue = rowValues[index + 1];
                                const columnName = Object.keys(priorYearProjectColumnNames).find(key => priorYearProjectColumnNames[key] === header);

                                if (columnName) {
                                    if (columnName === 'Investment') {
                                        totalInvestment += parseInvestment(cellValue);

                                    }
                                    if (columnName === 'ProjectName') {
                                        // calculate missing project name value based
                                        if (!cellValue) {
                                            totalMissingProjectNameValue += currentInvestment

                                        }
                                    }

                                    if (columnName === 'typeCast') {
                                        if (!cellValue) {
                                            totalMissingProjectTypeValue += currentInvestment
                                        }
                                    }

                                    if (columnName === 'StartDateConstruction' || columnName === 'EndDateConstruction' || columnName === "PeriodStartYear" || columnName === "PeriodEndYear") {
                                        // convert date to string with format DD/MM/YYYY
                                        // the excel upload will give us the date object
                                        if (cellValue instanceof Date) {
                                            cellValue = dayjs(cellValue).format('DD/MM/YYYY');
                                        }
                                        // calculate missing start dates value
                                        if (!cellValue && columnName === 'StartDateConstruction') {
                                            totalMissingConstructionStartDateValue += currentInvestment

                                        }

                                        // calculate missing end dates
                                        if (!cellValue && columnName === 'EndDateConstruction') {
                                            totalMissingConstructionEndDateValue += currentInvestment
                                        }
                                    }

                                    // change null or undefined to empty string
                                    if (!cellValue || cellValue === "null" || cellValue === "undefined") {
                                        cellValue = "";
                                    }
                                    rowObject[columnName] = cellValue;
                                }
                            });
                            jsonData.push(rowObject);
                        }
                    });
                    const sortedData = jsonData.sort((a, b) => {
                        // Sort based on empty string fields
                        const keysToSort = ['ProjectName', 'typeCast', 'StartDateConstruction', 'EndDateConstruction'];
                        for (let key of keysToSort) {
                            if (a[key] === '' && b[key] !== '') {
                                return -1;
                            } else if (a[key] !== '' && b[key] === '') {
                                return 1;
                            }
                        }
                        return 0;
                    });
                    setPreViewData(sortedData.map((project) => {
                        return {
                            ...project, LastUploadedDate: dayjs(lastUploadedDate).format('DD/MM/YYYY HH:mm:ss')
                        }
                    }));
                    setUpLoadInfo({
                        totalRecords: jsonData.length,
                        totalInvestment: formatCurrency(totalInvestment),
                        totalMissingProjectNameValue: formatCurrency(totalMissingProjectNameValue),
                        totalMissingProjectTypeValue: formatCurrency(totalMissingProjectTypeValue),
                        totalMissingConstructionStartDateValue: formatCurrency(totalMissingConstructionStartDateValue),
                        totalMissingConstructionEndDateValue: formatCurrency(totalMissingConstructionEndDateValue),
                    });

                    setCloseBatchFlag(false)
                    setPreViewFlag(true);

                }
            };
            reader.readAsArrayBuffer(file);

        } else {
            console.log('No file selected');
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const getCommonEditTextFieldProps = useCallback(
        (
            cell: MRT_Cell<PriorYearProject>,
        ): MRT_ColumnDef<PriorYearProject>['muiTableBodyCellEditTextFieldProps'] => {
            return {
                error: !!validationErrors[cell.id],
                helperText: validationErrors[cell.id],
            };
        },
        [validationErrors],
    );

    const columns = useMemo(() => statePriorYearBaselineConfig(priorYearProjectList, getCommonEditTextFieldProps), [priorYearProjectList, getCommonEditTextFieldProps]);

    // Download Excel
    useEffect(() => {

        function addDataToWorksheet(worksheet, data, startRow, validation = false) {

            function formatHeader(headerList) {
                return headerList.map((item) => priorYearProjectColumnNames[item]);
            }

            const processCell = (cell, colNumber, header) => {
                // Helper variables and functions
                const getColumnLetter = (col) => String.fromCharCode(64 + col);
                const cellRange = `${getColumnLetter(colNumber)}1:${getColumnLetter(colNumber)}${excelColumnMaxLength}`;
                const applyDataValidation = (range, formulae) => {
                    worksheet.dataValidations.add(range, {
                        type: 'list',
                        allowBlank: false,
                        formulae: formulae,
                    });
                };
                if (header === "KM" || header === "MW" || header === "M2") {
                    cell.value = Number(cell.value);
                } else if (header === "Construction Start Date" || header === "Construction End Date" || header === "Period Start Year" || header === "Period End Year") {
                    // cell.value = cell.value ? new Date(cell.value) : "";
                    // cell.numFmt = "dd/mm/yyyy";
                } else if (header === "Investment ($)" || header === "Commonwealth Funding") {
                    // if data contains $ or tbc or M like VIC data, this will break the download
                    //     cell.value = Number(cell.value);
                    //     cell.numFmt = '"$"#,##0.00';
                    // to fix this, we need to standardize the data user upload

                } else {
                    cell.value = String(cell.value);
                }

                if (header === "Project Type") {
                    applyDataValidation(cellRange, [`'Project Types'!$A$1:$A$${projectTypes.length}`]);
                }

                if (header === "Location") {
                    // INDIRECT will consider the note row and header row
                    applyDataValidation(cellRange, [`INDIRECT(D1)`])
                }

                if (header === "State") {
                    applyDataValidation(cellRange, [`'States'!$A$1:$A$${states.length}`]);
                };


                // change null or undefined to empty string
                if (!cell.value && typeof cell.value !== "number" || cell.value === "null" || cell.value === "undefined") {
                    cell.value = "";
                }
            }
            const processRow = (row, startRow) => {
                const newRow = worksheet.addRow(Object.values(row));
                newRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    const header = worksheet.getRow(startRow).getCell(colNumber).value;
                    processCell(cell, colNumber, header);
                });
            };

            const addHeaderRow = (data) => {
                worksheet.addRow(formatHeader(Object.keys(data[0])));
            };

            if (validation) {
                addHeaderRow(data);
                data.forEach((row) => processRow(row, startRow));
            } else {
                if (worksheet.name === "Locations") {
                    worksheet.addRow(states);
                    data.forEach((location, rowIndex) => {
                        const columnIndex = states.indexOf(location.state.toUpperCase()) + 1;
                        for (let i = 2; i <= rowIndex + 2; i++) {
                            let found = false;
                            const row = worksheet.getRow(i);
                            if (!row.getCell(columnIndex).value && !found) {
                                row.getCell(columnIndex).value = location.locationName;
                                found = true;
                                break;
                            }
                        }
                    });
                } else {
                    data.forEach((item) => worksheet.addRow([item]));
                }
            }
        }

        //@ts-ignore
        if (downloading && projectTypes.length !== 0 && locations.length !== 0) {
            //@ts-ignore

            const workbook = new Excel.Workbook();
            workbook.creator = "Infrastructure Australia";

            const worksheet = workbook.addWorksheet("State pipeline Project List");
            const worksheetProjectTypes = workbook.addWorksheet("Project Types");
            const worksheetLocations = workbook.addWorksheet("Locations");
            const worksheetStates = workbook.addWorksheet("States");


            priorYearProjectNote.forEach((line, index) => {
                const noteRow = worksheet.addRow([line]);
                noteRow.font = { bold: true };
            });

            if (statePipelineListData.length !== 0){
                addDataToWorksheet(worksheet, statePipelineListData, priorYearProjectNote.length + 1, true);
            }
            else{
                const header_row_obj = [
                                     {
                                         "Source": "",
                                         "ProjectID": "",
                                         "ProjectName": "",
                                         "State": "",
                                         "typeCast": "",
                                         "Investment": "",
                                         "MW": "",
                                         "KM": "",
                                         "M2": "",
                                         "ProjectStage": "",
                                         "DeliveryAgency": "",
                                         "ContractingModel": "",
                                         "StartDateConstruction": "",
                                         "EndDateConstruction": "",
                                         "project_location": "",
                                         "Latitude": "",
                                         "Longitude": "",
                                         "FundingMode": "",
                                         "sa4_code": "",
                                         "sa4_name": "",
                                         "CommonwealthFunding": "",
                                         "ProjectStatus": "",
                                         "PeriodStartYear": "",
                                         "PeriodEndYear": ""
                                     }
                                 ]
                addDataToWorksheet(worksheet, header_row_obj, priorYearProjectNote.length + 1, true);
            }

            addDataToWorksheet(worksheetProjectTypes, projectTypes, 1, false);
            addDataToWorksheet(worksheetLocations, locations, 1, false);
            addDataToWorksheet(worksheetStates, states, 1, false);


            const sortedLocationsByState = getSortedLocationByState(locations, states);

            // Create named ranges for each state in 'locations' worksheet
            Object.keys(sortedLocationsByState).forEach((state, colIndex) => {
                const colLetter = String.fromCharCode(65 + colIndex); // Convert column index to letter
                const range = `${colLetter}2:${colLetter}${sortedLocationsByState[state].length + 1}`;
                workbook.definedNames.add(`Locations!${range}`, state);
            });


            workbook.xlsx.writeBuffer().then((buffer) => {
                const blob = new Blob([buffer], { type: "application/xlsx" });
                saveAs(blob, "State Baseline.xlsx");
            });

            setDownloading(false);
        }
    }, [downloading]);


    return (
        <Box sx={{ zIndex: "1000" }}>
            <MaterialReactTable
                columns={columns}
                data={preViewFlag ? preViewData : statePipelineData ?? []}
                enableRowActions
                enableRowVirtualization
                rowVirtualizerProps={{ overscan: 10 }}
                enablePagination={false}
                enableEditing={false}
                onEditingRowCancel={() => { setValidationErrors({}) }}
                enableSorting={true}
                globalFilterFn="includesString"
                enableFacetedValues
                enableClickToCopy
                enableColumnResizing
                enableTopToolbar
                positionGlobalFilter="left"
                enableStickyHeader
                muiTableContainerProps={{ sx: { maxHeight: "50vh" } }}
                onShowColumnFiltersChange={(updater) => {
                    setShowColumnFilters((prev) =>
                        updater instanceof Function ? updater(prev) : updater,
                    );
                    setColumnFilters([]);
                    queueMicrotask(rerender); //hack to rerender after state update
                }}
                onColumnFiltersChange={(updater) => {
                    setColumnFilters((prev) =>
                        updater instanceof Function ? updater(prev) : updater,
                    );
                    queueMicrotask(rerender); //hack to rerender after state update
                }}
                onColumnVisibilityChange={(updater) => {
                    setColumnVisibility((prev) =>
                        updater instanceof Function ? updater(prev) : updater,
                    );
                    queueMicrotask(rerender); //hack to rerender after state update
                }}
                onRowSelectionChange={(updater) => {
                    setRowSelection((prev) =>
                        updater instanceof Function ? updater(prev) : updater,
                    );
                    queueMicrotask(rerender); //hack to rerender after state update
                }}
                initialState={{
                    showGlobalFilter: true,
                    density: "compact"
                }}
                state={{
                    columnVisibility,
                    rowSelection,
                    columnFilters,
                    showColumnFilters,
                    isLoading: callLastUploadedProjectList ? isLastUploadedProjectListLoading : isPriorYearProjectListLoading,
                    showAlertBanner: !!preViewFlag,
                    showProgressBars: isFetching,
                    columnPinning: {
                        right: [],
                        left: ['mrt-row-actions']
                    }
                }}
                muiToolbarAlertBannerProps={
                    !!preViewFlag ? {
                        color: "warning",
                        children: <>
                            <div>{`You have uploaded ${upLoadInfo.totalRecords} records, total ${upLoadInfo.totalInvestment}M investment, ${upLoadInfo.totalMissingProjectNameValue}M missing project name, ${upLoadInfo.totalMissingProjectTypeValue}M missing project type, ${upLoadInfo.totalMissingConstructionStartDateValue}M missing construction start date, ${upLoadInfo.totalMissingConstructionEndDateValue}M missing construction end date`}</div>
                            <div>Note: Projects with missing fields might be dropped from baseline</div>
                        </>

                    } : undefined
                }
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        Header: "", //change header text
                        size: 50, //make actions column smaller
                    },
                }}
                positionToolbarAlertBanner="top"
                tableInstanceRef={tableInstanceRef}
                muiTableBodyProps={{
                    sx: () => ({
                        "& tr:nth-of-type(odd)": {
                            backgroundColor: "#8578741A",
                        },
                    })
                }}
                positionActionsColumn="first"
                renderToolbarInternalActions={({ table }) => (
                    // render an empty action are to disable the duplicated actions(full screen etc.)
                    <Box flexWrap="nowrap">
                        <MRT_ShowHideColumnsButton table={table} />
                        <MRT_ToggleFiltersButton table={table} />
                        <MRT_FullScreenToggleButton table={table} />
                        <Tooltip arrow title={"Export All Data"} >
                            <label>
                                <Button disabled={downloading || isFetching}
                                    onClick={handleDownload} variant='outlined' endIcon={<FileDownloadIcon />}>DOWNLOAD</Button>
                            </label>
                        </Tooltip>
                        <input ref={fileInputRef} id='file-input' type='file' style={{ display: "none" }} onChange={handleFileChange}
                            accept=".xls,.xlsx,.csv" />
                        <Tooltip arrow title={"Import All Data"}>
                            <label htmlFor="file-input">
                                <Button sx={{ ml: 1 }}
                                    variant='outlined' component='span' disabled={isFetching} endIcon={<FileUpLoadIcon />}>UPLOAD
                                </Button>
                            </label>
                        </Tooltip>
                    </Box>
                )}
                renderBottomToolbarCustomActions={({ }) => {
                    return (
                        <Box sx={{ display: "flex", gap: "0.5rem", p: 2, justifyContent: "flex-end", width: "100%" }}>
                            <Box sx={{ fontStyle: "italic", fontWeight: 700, mt: 2, justifyContent: "flex-end", fontFamily: "ProximaNova-Bold", marginRight: "auto" }}>
                                {!!lastUploadedDate && <strong>{`Data last update ${lastUploadedDate} by ${lastUpdatedUser}`}</strong>}
                            </Box>
                            <Box >
                                <Button variant='contained' onClick={handleUpload} disabled={isFetching || !preViewFlag}>Submit</Button>
                            </Box>
                        </Box>
                    )
                }}
            />
            <Dialog open={validateUploadProjects.isLoading} aria-describedby='upload-progress' maxWidth="xs">
                <DialogTitle>The file is uploading...</DialogTitle>
                <DialogContent>
                    <LinearProgress id='upload-progress' />
                </DialogContent>
            </Dialog>
            <Dialog
                open={submitPopupOpen}
                onClose={() => setSubmitPopupOpen(false)}
                aria-describedby="alert-submit-description"
                maxWidth="xs"
            >
                <DialogContent>
                    <DialogContentText id="alert-submit-description">
                        {`You last uploaded a baseline at ${lastUploadedDate}. Upon submitting the pipeline, this will be subject to data quality and assurance process by IA. We will get in touch with you if there are any questions, and to confirm that it is replaced in the tool. Please confirm you are ready to submit your change? Yes/No`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelSubmit}>
                        No
                    </Button>
                    <Button onClick={handleConfirmSubmit} autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>);
}

export default StatePriorYearBaseline;
